import type { RouteLocationRaw, RouteLocationNormalized } from 'vue-router';

import {
    PartnersSettingsService,
} from '@evidentid/iam-client/services/partners-settings-service/partnersSettingsService';

// PRODUCT-26110 Phase 2B2: 'vue-router/types/router' is a vue-router-3-only subpath,
// not present in v4's `exports` map at all -- module-not-found, not just a type
// mismatch. `RawLocation` -> `RouteLocationRaw`, `Route` -> `RouteLocationNormalized`.
import { TermsState, UserState, RelyingParty } from './types';

interface AuthRedirection {
    to: RouteLocationRaw;
    replace: boolean;
}

interface AuthRedirectionOptions {
    user: UserState;
    route: RouteLocationNormalized;
    sign?: TermsState['signDetails'][keyof TermsState['signDetails']];
    getDefaultAuthenticatedRoute(route: RouteLocationNormalized): RouteLocationRaw;
}

enum AuthRouteMode {
    user = 'user',
    guest = 'guest',
    none = 'none',
}

interface AuthRouteDetails {
    mode: AuthRouteMode;
    url: string | null;
    internal: boolean;
    rpSpecific: boolean;
    rpId: string | null;
}

interface AuthUserDetails {
    mode: AuthRouteMode.guest | AuthRouteMode.user;
    ready: boolean;
    needsVerification: boolean;
    relyingParties: RelyingParty[];
}

function isRelyingPartyAvailable(rpId: string | null, relyingParties: RelyingParty[]) {
    return Boolean(rpId && relyingParties.find((rp) => rp.name === rpId));
}

function getLastRpName(): string | null {
    try {
        return localStorage.getItem('$lastSelectedRp') || null;
    } catch (error) {
        return null;
    }
}

function getPreferredRelyingPartyName(relyingParties: RelyingParty[]) {
    const lastRpName = getLastRpName();
    return lastRpName && relyingParties.find((rp) => rp.name === lastRpName)
        ? lastRpName
        : relyingParties.length > 0 ? relyingParties[0].name : null;
}

function getRouteDetails(route: RouteLocationNormalized): AuthRouteDetails {
    // vue-router 4 types route names as `RouteRecordName = string | symbol`, stricter
    // than vue-router 3's bare `string` -- coerce here, matching ed2202df8's pattern.
    const internal = /^auth\./.test(String(route.name || ''));
    const rpSpecific = /\/:rpId\??\//.test(route.matched[0]?.path || '');
    const rpId = typeof route.params.rpId === 'string' ? route.params.rpId : null;
    const guestOnly = Boolean(route.meta?.guestOnly);
    const userOnly = Boolean(route.meta?.userOnly || rpSpecific);
    const mode = userOnly ? AuthRouteMode.user : guestOnly ? AuthRouteMode.guest : AuthRouteMode.none;
    const next = typeof route.query.next === 'string' ? route.query.next : null;
    const url = internal
        ? next || null
        : route.fullPath;

    if (guestOnly && userOnly) {
        throw new Error('The route can\'t be registered as both guest and user only!');
    }

    return { mode, url, internal, rpSpecific, rpId };
}

function getUserDetails(user: UserState): AuthUserDetails {
    const ready = user.initialized && Boolean(!user.data || user.relyingParties);
    const relyingParties = user.relyingParties || [];
    const mode = user.data ? AuthRouteMode.user : AuthRouteMode.guest;
    const needsVerification = Boolean(user.data && !user.data.verified);

    return { mode, ready, needsVerification, relyingParties };
}

export function isEvidentUser(email: string | null | undefined): boolean {
    return /@evidentid\.com$/.test(email || '');
}

export function getAuthRedirection(options: AuthRedirectionOptions): AuthRedirection | null {
    // Extract base information
    const { user, route, sign } = options;
    const defaultAuthenticatedRoute = options.getDefaultAuthenticatedRoute(route);
    const routeDetails = getRouteDetails(route);
    const userDetails = getUserDetails(user);
    const isCurrentUserEvident = isEvidentUser(user.data?.email);
    const partnersSettings = PartnersSettingsService.getPartnerSettings();

    if ((route.name === 'error' && route.params.reason === 'unactivated') || (user.data?.accountNotActivated)) {
        return null;
    }
    // Basic page, which doesn't have any auth expectations
    if (routeDetails.mode === AuthRouteMode.none && !routeDetails.internal) {
        return null;
    }

    if (userDetails.needsVerification) {
        if (route.name === 'auth.verify') {
            return null;
        }
        return {
            to: { name: 'auth.verify', query: { next: routeDetails.url, force: '' } },
            replace: true,
        };
    } else if (route.name === 'auth.verify') {
        return {
            to: routeDetails.url || defaultAuthenticatedRoute,
            replace: false,
        };
    }

    if (!userDetails.ready) {
        if (route.name === 'auth.check' || route.name === 'error.rp') {
            return null;
        }
        return {
            to: { name: 'auth.check', query: { next: routeDetails.url, force: '' } },
            replace: true,
        };
    } else if (route.name === 'auth.check') {
        return {
            to: routeDetails.url || defaultAuthenticatedRoute,
            replace: false,
        };
    }

    if (userDetails.mode === AuthRouteMode.guest && routeDetails.mode === AuthRouteMode.user) {
        return {
            to: { name: 'auth.login', query: { next: routeDetails.url, force: '' } },
            replace: true,
        };
    }

    if (userDetails.mode === AuthRouteMode.user && routeDetails.mode === AuthRouteMode.guest) {
        return {
            to: defaultAuthenticatedRoute,
            replace: false,
        };
    }

    // Restrict to EvidentID staff (@evidentid.com) when route has evidentOnly meta
    if (route.meta?.evidentOnly && user.data && !isCurrentUserEvident) {
        return {
            to: defaultAuthenticatedRoute,
            replace: false,
        };
    }

    if (!routeDetails.rpSpecific) {
        return null;
    }

    if (userDetails.relyingParties.length === 0) {
        if (route.name === 'auth.noRelyingParties') {
            return null;
        }
        return {
            to: { name: 'auth.noRelyingParties', query: { next: routeDetails.url, force: '' } },
            replace: true,
        };
    } else if (route.name === 'auth.noRelyingParties') {
        return {
            to: routeDetails.url || defaultAuthenticatedRoute,
            replace: false,
        };
    }

    if (!routeDetails.rpId || !isRelyingPartyAvailable(routeDetails.rpId, userDetails.relyingParties)) {
        const rpId = getPreferredRelyingPartyName(userDetails.relyingParties);
        return {
            to: { ...route, params: { ...route.params, rpId } } as any,
            replace: true,
        };
    }

    // Handle RP-specific errors nicely
    if (route.name === 'error.rp') {
        return null;
    }

    const isEmailWhitelisted = partnersSettings
        ?.whitelisted_domains
        ?.includes(user.data?.email?.split('@')?.[1] || '');
    // Check if RP has terms accepted
    if (!isCurrentUserEvident && !sign?.signedAt && !isEmailWhitelisted) {
        if (/^auth\.terms(?:\.redirect)?$/.test(String(route.name || ''))) {
            return null;
        }
        return {
            to: {
                name: 'auth.terms.redirect',
                params: { rpId: routeDetails.rpId },
                query: { next: routeDetails.url, force: '' },
            },
            replace: true,
        };
    }

    // Handle finished acceptation
    if (route.name === 'auth.terms.redirect') {
        if (routeDetails.url) {
            return { to: routeDetails.url, replace: true };
        }
        return {
            to: {
                name: 'auth.terms',
                params: { rpId: routeDetails.rpId, force: '' },
            },
            replace: true,
        };
    }

    return null;
}
